import React from 'react';
import { IntervalReport, IntervalTemplate, Report } from 'api/report/types';
import {
    createEmptyIntervalEntryDraft,
    createIntervalReportDraft,
    IntervalEntryDraft,
    IntervalEntryValidations,
    IntervalReportDraft,
    serializeIntervalDraftValues,
    validateIntervalReportDraft,
} from 'report/logic/interval/editing';

interface Props {
    report: Report;
    children: React.ReactNode;
}

interface IntervalDraftContextValue {
    sourceReport: Report;
    draft: IntervalReportDraft;
    canAddEntry: boolean;
    addEntry: (date: string) => string | null;
    updateEntry: (
        id: string,
        update: React.SetStateAction<IntervalEntryDraft>
    ) => void;
    deleteEntry: (id: string) => void;
    setNotes: (notes: string) => void;
    originalEntries: IntervalEntryDraft[];
    entryValidation: IntervalEntryValidations;
    hasChanges: boolean;
}

const IntervalDraftContext =
    React.createContext<IntervalDraftContextValue | null>(null);

function findEntryPosition(
    draft: IntervalReportDraft,
    id: string
): { dayIndex: number; entryIndex: number } | null {
    for (let dayIndex = 0; dayIndex < draft.days.length; dayIndex++) {
        const entryIndex = draft.days[dayIndex].entries.findIndex(
            entry => entry.uuid === id
        );
        if (entryIndex >= 0) {
            return { dayIndex, entryIndex };
        }
    }
    return null;
}

export function IntervalDraftProvider({ report, children }: Props) {
    const initialDraft = React.useMemo(
        () => createIntervalReportDraft(report.data as IntervalReport),
        [report.data]
    );
    const initialValues = React.useMemo(
        () => serializeIntervalDraftValues(initialDraft),
        [initialDraft]
    );
    const [draft, setDraft] = React.useState(initialDraft);

    React.useEffect(() => {
        setDraft(initialDraft);
    }, [initialDraft]);

    const originalEntries = React.useMemo(
        () => initialDraft.days.flatMap(day => day.entries),
        [initialDraft]
    );
    const entryKinds = (report.template.data as IntervalTemplate).entryKinds;
    const entryValidation = React.useMemo(
        () => validateIntervalReportDraft(draft, entryKinds),
        [draft, entryKinds]
    );
    const canAddEntry =
        draft.days.length > 0 &&
        entryKinds.length > 0 &&
        Object.values(entryValidation).every(
            ({ errors }) => Object.keys(errors).length === 0
        );

    const addEntry = (date: string): string | null => {
        const config = entryKinds[0];
        if (
            !canAddEntry ||
            !config ||
            !draft.days.some(day => day.date === date)
        ) {
            return null;
        }
        const entry = createEmptyIntervalEntryDraft(
            crypto.randomUUID(),
            config.kind,
            date
        );
        setDraft(previous => ({
            ...previous,
            days: previous.days.map(day =>
                day.date === date
                    ? { ...day, entries: [...day.entries, entry] }
                    : day
            ),
        }));
        return entry.uuid;
    };

    const updateEntry = React.useCallback(
        (id: string, update: React.SetStateAction<IntervalEntryDraft>) => {
            setDraft(previous => {
                const position = findEntryPosition(previous, id);
                if (!position) return previous;
                const { dayIndex, entryIndex } = position;
                const day = previous.days[dayIndex];
                const current = day.entries[entryIndex];
                const next =
                    typeof update === 'function' ? update(current) : update;
                if (next === current) return previous;

                const entries = [...day.entries];
                entries[entryIndex] = next;
                const days = [...previous.days];
                days[dayIndex] = { ...day, entries };
                return { ...previous, days };
            });
        },
        []
    );

    const deleteEntry = React.useCallback((id: string) => {
        setDraft(previous => {
            const position = findEntryPosition(previous, id);
            if (!position) return previous;
            const { dayIndex, entryIndex } = position;
            const day = previous.days[dayIndex];
            const entries = [...day.entries];
            entries.splice(entryIndex, 1);
            const days = [...previous.days];
            days[dayIndex] = { ...day, entries };
            return { ...previous, days };
        });
    }, []);

    const setNotes = React.useCallback((notes: string) => {
        setDraft(previous => ({ ...previous, notes }));
    }, []);

    return (
        <IntervalDraftContext.Provider
            value={{
                sourceReport: report,
                draft,
                canAddEntry,
                addEntry,
                updateEntry,
                deleteEntry,
                setNotes,
                originalEntries,
                entryValidation,
                hasChanges:
                    serializeIntervalDraftValues(draft) !== initialValues,
            }}>
            {children}
        </IntervalDraftContext.Provider>
    );
}

export function useIntervalDraft(): IntervalDraftContextValue {
    const draft = React.useContext(IntervalDraftContext);
    if (!draft) {
        throw new Error(
            'Interval must be rendered inside IntervalDraftProvider.'
        );
    }
    return draft;
}
